"use client";

import { RiLoginBoxLine, RiUserAddLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { useRouter } from "next/navigation";
import { memo, useCallback, useEffect, useRef } from "react";

interface AuthPromptModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	message?: string;
	redirectPath?: string;
	actionType?: "view_details" | "save_sets" | "add_to_list" | "general";
}

export const AuthPromptModal = memo(function AuthPromptModal({
	open,
	onOpenChange,
	title,
	message,
	redirectPath = "",
	actionType = "general",
}: AuthPromptModalProps) {
	const router = useRouter();
	const isMobile = useRef(false);

	// Get contextual messaging based on action type
	const getContextualContent = () => {
		switch (actionType) {
			case "view_details":
				return {
					title: title || "Sign in to view set details",
					message:
						message ||
						"Create an account or sign in to save sets to your schedule and view detailed information.",
				};
			case "save_sets":
				return {
					title: title || "Sign in to save sets",
					message:
						message ||
						"Create an account to save sets to your personal festival schedule and never miss a performance.",
				};
			case "add_to_list":
				return {
					title: title || "Sign in to add to your list",
					message:
						message ||
						"Create an account to save sets and build your personal festival schedule.",
				};
			default:
				return {
					title: title || "Sign in to continue",
					message:
						message ||
						"Create an account or sign in to unlock all features and track your festival experiences.",
				};
		}
	};

	const { title: contextualTitle, message: contextualMessage } =
		getContextualContent();

	// Check if mobile on mount and window resize
	useEffect(() => {
		const checkMobile = () => {
			isMobile.current = window.innerWidth < 768;
		};
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const handleSignIn = useCallback(() => {
		onOpenChange(false);
		const redirectParam = redirectPath
			? `?next=${encodeURIComponent(redirectPath)}`
			: "";
		router.push(`/auth/signin${redirectParam}`);
	}, [onOpenChange, router, redirectPath]);

	const handleSignUp = useCallback(() => {
		onOpenChange(false);
		const redirectParam = redirectPath
			? `?next=${encodeURIComponent(redirectPath)}`
			: "";
		router.push(`/auth/signup${redirectParam}`);
	}, [onOpenChange, router, redirectPath]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md">
				<DialogTitle className="sr-only">Sign in to continue</DialogTitle>
				<DialogDescription className="sr-only">
					Sign in or create an account to access this feature
				</DialogDescription>
				<DialogHeader className="px-6 pt-6 pb-0">
					<h2 className="font-semibold leading-none tracking-tight">
						{contextualTitle}
					</h2>
					<p className="text-muted-foreground text-sm">{contextualMessage}</p>
				</DialogHeader>

				<div className="space-y-6 px-6 py-6">
					<div className="flex flex-col gap-3">
						<Button onClick={handleSignUp} className="w-full" size="lg">
							<RiUserAddLine className="h-4 w-4" />
							Sign Up
						</Button>

						<Button
							onClick={handleSignIn}
							variant="outline"
							className="w-full"
							size="lg"
						>
							<RiLoginBoxLine className="h-4 w-4" />
							Sign In
						</Button>
					</div>

					<div className="text-center">
						<p className="text-muted-foreground text-xs">
							Join hundreds of live music fans sharing their experiences
						</p>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
});
